import { useFrame, useThree } from '@react-three/fiber'
import { forwardRef, useRef, useState } from 'react'
import type { Mesh } from 'three'
import { Plane, Quaternion, RepeatWrapping, TextureLoader, Vector2, Vector3 } from 'three'

const WALL_COLOR = '#efede8'

const configureWallTexture = (texture: THREE.Texture) => {
  texture.wrapT = RepeatWrapping
  texture.wrapS = RepeatWrapping
  texture.repeat.set(4, 4)
  texture.flipY = true
}

const textureLoader = new TextureLoader()
const loadWallTexture = (url: string) => {
  const absUrl = new URL(url, phoenix.runtimeConfig.RESOURCES_URL ?? '').href

  return textureLoader.load(absUrl, texture => {
    configureWallTexture(texture)
  })
}

const wallMapTexture = loadWallTexture('textures/plaster-wall-smooth-grayish_BaseColor.jpg')
const wallRoughnessTexture = loadWallTexture('textures/plaster-wall-smooth-grayish_Roughness.jpg')
const wallNormalTexture = loadWallTexture('textures/plaster-wall-smooth-grayish_Normal.jpg')

interface WallCornerObjectProps {
  height: number
  thickness: number
}

export const WallCornerObject = forwardRef<Mesh, WallCornerObjectProps>(
  ({ height, thickness }: WallCornerObjectProps, ref) => {
    return (
      <mesh ref={ref} position={[thickness / 2, height / 2, -thickness / 2]}>
        <boxGeometry args={[thickness, height, thickness]} />
        <meshStandardMaterial
          color={WALL_COLOR}
          map={wallMapTexture}
          roughness={1.5}
          roughnessMap={wallRoughnessTexture}
          normalMap={wallNormalTexture}
          normalScale={new Vector2(1, 1)}
        />
      </mesh>
    )
  }
)

interface WallProps {
  length: number
  height: number
  hiddenHeight: number
  thickness: number
  cameraHiding: boolean
}

export const isInFrontOf = (plane: Plane, viewPos: Vector3) => plane.normal.dot(viewPos) + plane.constant > 0

export const Wall = ({ length, height, hiddenHeight, thickness, cameraHiding }: WallProps) => {
  const ref = useRef<Mesh>(null)

  const [inFrontOf, setInFrontOf] = useState(true)

  const { camera } = useThree()

  useFrame(() => {
    if (!ref.current) {
      return
    }

    const cameraWorldPosition = new Vector3()
    camera.getWorldPosition(cameraWorldPosition)

    const normalVector = new Vector3(0, 0, 1)

    const worldNormal = ref.current.parent!.localToWorld(normalVector)

    worldNormal.normalize()

    const parentWorldPosition = new Vector3()
    ref.current.parent!.getWorldPosition(parentWorldPosition)

    const worldRotation = new Quaternion()
    ref.current.parent!.getWorldQuaternion(worldRotation)

    const rotatedNormal = new Vector3(0, 0, 1).applyQuaternion(worldRotation)

    const constant = parentWorldPosition.dot(rotatedNormal)
    const plane = new Plane(rotatedNormal, -constant)

    const inFrontOf = isInFrontOf(plane, cameraWorldPosition)

    setInFrontOf(inFrontOf)
  })

  const effectiveHeight = !cameraHiding || inFrontOf ? height : hiddenHeight

  return (
    <>
      <mesh ref={ref} position={[length / 2, effectiveHeight / 2, -thickness / 2]} rotation={[0, Math.PI / 2, 0]}>
        <boxGeometry args={[thickness, effectiveHeight, length]} />
        <meshStandardMaterial
          color={WALL_COLOR}
          map={wallMapTexture}
          roughness={1.5}
          roughnessMap={wallRoughnessTexture}
          normalMap={wallNormalTexture}
          normalScale={new Vector2(1, 1)}
        />
      </mesh>
    </>
  )
}
